import { RRule } from 'rrule'

/**
 * Day code mapping for RRULE
 */
const DAY_MAP = {
  MO: RRule.MO,
  TU: RRule.TU,
  WE: RRule.WE,
  TH: RRule.TH,
  FR: RRule.FR,
  SA: RRule.SA,
  SU: RRule.SU,
}

/**
 * Reverse mapping for parsing
 */
const DAY_CODE_MAP = {
  0: 'MO',
  1: 'TU',
  2: 'WE',
  3: 'TH',
  4: 'FR',
  5: 'SA',
  6: 'SU',
}

/**
 * Day names for human-readable descriptions
 */
const DAY_NAMES = {
  MO: 'Monday',
  TU: 'Tuesday',
  WE: 'Wednesday',
  TH: 'Thursday',
  FR: 'Friday',
  SA: 'Saturday',
  SU: 'Sunday',
}

/**
 * Generates an RRULE string from user selections
 * @param {Object} options
 * @param {string[]} options.days - Array of day codes ['MO', 'TU', ...]
 * @param {number} options.interval - Week interval (1 = every week, 2 = every other week)
 * @param {Date} options.startDate - First occurrence date
 * @param {Date} [options.endDate] - Optional end date
 * @returns {string} - RRULE string
 */
export function generateRRule({ days, interval = 1, startDate, endDate }) {
  if (!days || days.length === 0) {
    throw new Error(
      'At least one day must be selected for recurring availability',
    )
  }

  const byweekday = days.map((d) => DAY_MAP[d])

  const ruleOptions = {
    freq: RRule.WEEKLY,
    interval,
    byweekday,
    dtstart: startDate,
  }

  if (endDate) {
    ruleOptions.until = endDate
  }

  const rule = new RRule(ruleOptions)
  const fullString = rule.toString()

  // Strip DTSTART line since we use the database start_time to determine actual times
  // Only keep the RRULE line which has the pattern (BYDAY, FREQ, etc)
  const rruleLine = fullString
    .split('\n')
    .find((line) => line.startsWith('RRULE:'))

  if (!rruleLine) {
    throw new Error('Failed to generate RRULE string')
  }

  return rruleLine
}

/**
 * Parses an RRULE string back to user-friendly options
 * @param {string} rruleString
 * @returns {Object} - { days, interval, endDate }
 */
export function parseRRule(rruleString) {
  try {
    const rule = RRule.fromString(rruleString)

    const days = (rule.options.byweekday || []).map((d) => {
      // Handle both numeric weekday values and RRule weekday objects
      const dayNum = typeof d === 'number' ? d : d.weekday
      return DAY_CODE_MAP[dayNum]
    })

    return {
      days,
      interval: rule.options.interval || 1,
      endDate: rule.options.until || null,
    }
  } catch (error) {
    console.error('Failed to parse RRULE:', error)
    return {
      days: [],
      interval: 1,
      endDate: null,
    }
  }
}

/**
 * Generates a human-readable description of the recurrence
 * @param {string[]} days - Day codes
 * @param {number} interval - Week interval
 * @param {Date} [endDate] - End date
 * @returns {string}
 */
export function describeRecurrence(days, interval, endDate) {
  if (!days || days.length === 0) {
    return ''
  }

  const dayList = days.map((d) => DAY_NAMES[d])
  let description = 'Every '

  if (interval > 1) {
    description += `${interval} weeks on `
  }

  if (dayList.length === 1) {
    description += dayList[0]
  } else if (dayList.length === 2) {
    description += `${dayList[0]} and ${dayList[1]}`
  } else {
    const last = dayList[dayList.length - 1]
    const allButLast = dayList.slice(0, -1)
    description += `${allButLast.join(', ')}, and ${last}`
  }

  if (endDate) {
    const dateStr = endDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
    description += ` until ${dateStr}`
  }

  return description
}

/**
 * Counts the number of occurrences that will be generated
 * @param {Object} options
 * @param {string[]} options.days - Array of day codes
 * @param {number} options.interval - Week interval
 * @param {Date} options.startDate - First occurrence date
 * @param {Date} options.endDate - End date
 * @returns {number} - Number of occurrences
 */
export function countOccurrences({ days, interval, startDate, endDate }) {
  if (!days || days.length === 0 || !startDate || !endDate) {
    return 0
  }

  try {
    const byweekday = days.map((d) => DAY_MAP[d])

    const rule = new RRule({
      freq: RRule.WEEKLY,
      interval,
      byweekday,
      dtstart: startDate,
      until: endDate,
    })

    return rule.all().length
  } catch (error) {
    console.error('Failed to count occurrences:', error)
    return 0
  }
}

/**
 * Expands a recurring availability into concrete time slots within a date range
 * Filters out any exception dates stored in exception_dates array
 * @param {Object} availability - Availability record with is_recurring, recurrence_rule, and exception_dates
 * @param {Date} rangeStart - Start of date range to expand
 * @param {Date} rangeEnd - End of date range to expand
 * @returns {Array} - Array of {start_time, end_time, ...originalFields} objects
 */
export function expandRecurrence(availability, rangeStart, rangeEnd) {
  // Non-recurring availability: return as-is
  if (!availability.is_recurring || !availability.recurrence_rule) {
    return [availability]
  }

  try {
    // Calculate duration from original start/end times
    const originalStart = new Date(availability.start_time)
    const originalEnd = new Date(availability.end_time)
    const durationMs = originalEnd - originalStart

    // Extract time of day from original start time IN LOCAL TIME
    // This ensures recurring times stay consistent (e.g., "Tuesday 7pm" remains 7pm local)
    const hours = originalStart.getHours()
    const minutes = originalStart.getMinutes()
    const seconds = originalStart.getSeconds()
    const milliseconds = originalStart.getMilliseconds()

    // FIX: RRule uses UTC for day-of-week calculations. When the local time is
    // near midnight UTC (e.g., 7pm EST = 00:00 UTC next day), the UTC day differs
    // from the local day, causing BYDAY mismatches (e.g., Tuesday 7pm EST = Wednesday UTC).
    // Solution: Use noon local time for dtstart, which is always on the same calendar
    // day in both local and UTC (for any reasonable timezone ±12 hours from UTC).
    // We'll restore the correct time when mapping occurrences below.
    const noonStart = new Date(originalStart)
    noonStart.setHours(12, 0, 0, 0)

    // Parse the RRULE string directly to options (don't use fromString which creates an RRule instance)
    // Then set dtstart to noon on the start date to fix UTC day-of-week matching
    const parsedOptions = RRule.parseString(availability.recurrence_rule)
    parsedOptions.dtstart = noonStart
    const ruleWithStart = new RRule(parsedOptions)

    // Get all occurrences in the date range
    const occurrences = ruleWithStart.between(rangeStart, rangeEnd, true)

    // Parse exception dates if they exist
    const exceptionDates = availability.exception_dates
      ? Array.isArray(availability.exception_dates)
        ? availability.exception_dates
        : JSON.parse(availability.exception_dates)
      : []

    // Convert exception dates to UTC date strings for comparison
    const exceptionDateStrings = exceptionDates.map((d) => {
      const date = new Date(d)
      return date.toISOString().split('T')[0] // YYYY-MM-DD format
    })

    // Map each occurrence to a concrete time slot, then filter by adjusted date
    const expanded = occurrences
      .map((occurrence) => {
        // Set the time of day to match the original start time IN LOCAL TIME
        // This preserves the user's intent (e.g., "Tuesday 7pm" stays at 7pm local every week)
        const adjustedStart = new Date(occurrence)
        adjustedStart.setHours(hours, minutes, seconds, milliseconds)

        const slot = {
          ...availability,
          start_time: adjustedStart,
          end_time: new Date(adjustedStart.getTime() + durationMs),
          // Mark as expanded so we can distinguish from original
          _expanded: true,
          _original_id: availability.id,
          _original_date: occurrence.toISOString(), // Track which date this instance represents
        }

        return slot
      })
      .filter((slot) => {
        // Check if this ADJUSTED slot date is in the exceptions list
        // This ensures exceptions match what the user sees (not the noon occurrence date)
        const slotDateStr = slot.start_time.toISOString().split('T')[0]
        const isException = exceptionDateStrings.includes(slotDateStr)
        return !isException
      })

    return expanded
  } catch (error) {
    // If RRULE parsing fails, log error and return original slot
    console.error('Error expanding recurrence rule:', error)
    return [availability]
  }
}
